import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { MapLibreMap, MapMarker, MapPolygon } from '@molecules/index';
import { Card } from '@atoms/index';
import { useSmoothMarker } from '@/hooks/useSmoothMarker';
import { Map, GeoJSONSource } from 'maplibre-gl';

interface LiveOrderMapProps {
  restaurantLat: number;
  restaurantLng: number;
  deliveryLat: number;
  deliveryLng: number;
  courierLat: number | null;
  courierLng: number | null;
  courierBearing?: number | null;
  courierRemainingTime?: number | null;
  routePath?: number[][] | null;
  orderStatus: string;
}

const isValidCoord = (val: number | null | undefined): val is number => {
  return val != null && !isNaN(Number(val)) && 
         Number(val) >= -90 && Number(val) <= 90;
};

const isValidLng = (val: number | null | undefined): val is number => {
  return val != null && !isNaN(Number(val)) && 
         Number(val) >= -180 && Number(val) <= 180;
};

export const LiveOrderMap = ({
  restaurantLat,
  restaurantLng,
  deliveryLat,
  deliveryLng,
  courierLat,
  courierLng,
  courierBearing,
  courierRemainingTime,
  routePath,
  orderStatus,
}: LiveOrderMapProps) => {
  const routePathRestaurantLat = useMemo(() => {
    if (routePath && routePath.length > 0 && Array.isArray(routePath[0]) && routePath[0].length >= 2) {
      const lat = Number(routePath[0][0]);
      const lng = Number(routePath[0][1]);
      if (isValidCoord(lat) && isValidLng(lng)) {
        return { lat, lng };
      }
    }
    return null;
  }, [routePath]);

  const effectiveRestaurantLat = routePathRestaurantLat?.lat ?? restaurantLat;
  const effectiveRestaurantLng = routePathRestaurantLat?.lng ?? restaurantLng;

  const validRestaurantLat = isValidCoord(effectiveRestaurantLat) ? Number(effectiveRestaurantLat) : null;
  const validRestaurantLng = isValidLng(effectiveRestaurantLng) ? Number(effectiveRestaurantLng) : null;
  const validDeliveryLat = isValidCoord(deliveryLat) ? Number(deliveryLat) : null;
  const validDeliveryLng = isValidLng(deliveryLng) ? Number(deliveryLng) : null;
  const validCourierLat = isValidCoord(courierLat) ? Number(courierLat) : null;
  const validCourierLng = isValidLng(courierLng) ? Number(courierLng) : null;

  const ISTANBUL_CENTER: [number, number] = [28.9784, 41.0082];

  const [mapInstance, setMapInstance] = useState<Map | null>(null);
  const [initialCenter, setInitialCenter] = useState<[number, number]>(() => {
    if (validRestaurantLat && validRestaurantLng && validDeliveryLat && validDeliveryLng) {
      return [
        (validRestaurantLng + validDeliveryLng) / 2,
        (validRestaurantLat + validDeliveryLat) / 2,
      ];
    }
    return ISTANBUL_CENTER;
  });

  const [initialZoom] = useState(13);
  const mapInitializedRef = useRef(false);
  const [autoPanEnabled, setAutoPanEnabled] = useState(true);
  const [showRecenterButton, setShowRecenterButton] = useState(false);
  const autoPanReenableTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastUserInteractionRef = useRef<number>(Date.now());

  const showCourier = orderStatus === 'ON_WAY' || orderStatus === 'DELIVERED';
  
  const courierTargetLat = showCourier && validCourierLat !== null ? validCourierLat : (showCourier && validRestaurantLat !== null ? validRestaurantLat : null);
  const courierTargetLng = showCourier && validCourierLng !== null ? validCourierLng : (showCourier && validRestaurantLng !== null ? validRestaurantLng : null);
  
  const hasCourierLocation = validCourierLat !== null && validCourierLng !== null;
  
  useEffect(() => {
    if (showCourier) {
      console.log('🎯 Courier target coordinates for useSmoothMarker:', {
        courierTargetLat,
        courierTargetLng,
        source: validCourierLat !== null ? 'courierLocation' : 'restaurant',
        validCourierLat,
        validCourierLng,
        validRestaurantLat,
        validRestaurantLng,
        rawCourierLat: courierLat,
        rawCourierLng: courierLng,
        rawRestaurantLat: restaurantLat,
        rawRestaurantLng: restaurantLng,
        routePathRestaurantLat: routePathRestaurantLat?.lat,
        routePathRestaurantLng: routePathRestaurantLat?.lng,
        usingRoutePathForRestaurant: routePathRestaurantLat !== null,
      });
    }
  }, [showCourier, courierTargetLat, courierTargetLng, validCourierLat, validCourierLng, validRestaurantLat, validRestaurantLng, courierLat, courierLng, restaurantLat, restaurantLng, routePathRestaurantLat]);
  
  const smoothPosition = useSmoothMarker(
    courierTargetLat, 
    courierTargetLng, 
    courierBearing !== null && courierBearing !== undefined ? courierBearing : null,
    3000
  );
  
  useEffect(() => {
    if (smoothPosition) {
      console.log('📍 useSmoothMarker returned position:', {
        lat: smoothPosition.lat,
        lng: smoothPosition.lng,
        bearing: smoothPosition.bearing,
      });
    }
  }, [smoothPosition]);

  useEffect(() => {
    if (mapInitializedRef.current) return;

    if (validRestaurantLat && validRestaurantLng && validDeliveryLat && validDeliveryLng) {
      setInitialCenter([
        (validRestaurantLng + validDeliveryLng) / 2,
        (validRestaurantLat + validDeliveryLat) / 2,
      ]);
    }
    
    mapInitializedRef.current = true;
  }, [validRestaurantLat, validRestaurantLng, validDeliveryLat, validDeliveryLng]);

  const courierPosition = useMemo(() => {
    if (showCourier && hasCourierLocation && smoothPosition && isValidCoord(smoothPosition.lat) && isValidLng(smoothPosition.lng)) {
      console.log('📍 Using smoothPosition for courier (has courier location):', {
        lat: smoothPosition.lat,
        lng: smoothPosition.lng,
        bearing: smoothPosition.bearing,
        source: 'smoothPosition',
      });
      return smoothPosition;
    }
    if (showCourier && !hasCourierLocation && validRestaurantLat !== null && validRestaurantLng !== null && routePathRestaurantLat !== null) {
      const restaurantPos = { lat: validRestaurantLat, lng: validRestaurantLng };
      console.log('📍 Using restaurant position for courier (no courier location yet):', {
        lat: restaurantPos.lat,
        lng: restaurantPos.lng,
        source: 'restaurant',
        usingRoutePathRestaurant: true,
      });
      return { ...restaurantPos, bearing: null };
    }
    if (showCourier && smoothPosition && isValidCoord(smoothPosition.lat) && isValidLng(smoothPosition.lng)) {
      console.log('📍 Using smoothPosition for courier (fallback):', {
        lat: smoothPosition.lat,
        lng: smoothPosition.lng,
        bearing: smoothPosition.bearing,
        source: 'smoothPosition-fallback',
      });
      return smoothPosition;
    }
    return null;
  }, [showCourier, hasCourierLocation, smoothPosition, validRestaurantLat, validRestaurantLng, routePathRestaurantLat]);
  
  const showCourierMarker = courierPosition !== null && (routePathRestaurantLat !== null || hasCourierLocation);

  useEffect(() => {
    if (showCourier) {
      console.log('🔍 Courier visibility check:', {
        orderStatus,
        showCourier,
        validCourierLat,
        validCourierLng,
        courierPosition,
        showCourierMarker,
      });
    }
  }, [showCourier, orderStatus, validCourierLat, validCourierLng, courierPosition, showCourierMarker]);

  const handleUserInteraction = useCallback(() => {
    setAutoPanEnabled(false);
    setShowRecenterButton(true);
    lastUserInteractionRef.current = Date.now();

    if (autoPanReenableTimeoutRef.current) {
      clearTimeout(autoPanReenableTimeoutRef.current);
    }

    autoPanReenableTimeoutRef.current = setTimeout(() => {
      setAutoPanEnabled(true);
      setShowRecenterButton(false);
    }, 10000);
  }, []);

  const handleRecenter = useCallback(() => {
    if (mapInstance && smoothPosition && isValidCoord(smoothPosition.lat) && isValidLng(smoothPosition.lng)) {
      mapInstance.flyTo({
        center: [smoothPosition.lng, smoothPosition.lat],
        zoom: mapInstance.getZoom(),
        duration: 1000,
      });
    }
    setAutoPanEnabled(true);
    setShowRecenterButton(false);
  }, [mapInstance, smoothPosition]);

  useEffect(() => {
    if (!mapInstance) return;

    const handleDragStart = () => handleUserInteraction();
    const handleZoomStart = () => handleUserInteraction();

    mapInstance.on('dragstart', handleDragStart);
    mapInstance.on('zoomstart', handleZoomStart);

    return () => {
      mapInstance.off('dragstart', handleDragStart);
      mapInstance.off('zoomstart', handleZoomStart);
    };
  }, [mapInstance, handleUserInteraction]);

  useEffect(() => {
    if (!mapInstance || !autoPanEnabled || !showCourierMarker || !smoothPosition) return;

    const checkAndPan = () => {
      const timeSinceInteraction = Date.now() - lastUserInteractionRef.current;
      const INTERACTION_COOLDOWN = 5000;

      if (timeSinceInteraction >= INTERACTION_COOLDOWN && smoothPosition) {
        mapInstance.panTo([smoothPosition.lng, smoothPosition.lat], {
          duration: 1000,
        });
      }
    };

    const interval = setInterval(checkAndPan, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [mapInstance, autoPanEnabled, showCourierMarker, smoothPosition]);

  useEffect(() => {
    return () => {
      if (autoPanReenableTimeoutRef.current) {
        clearTimeout(autoPanReenableTimeoutRef.current);
      }
    };
  }, []);

  const handleMapLoad = useCallback((map: Map) => {
    setMapInstance(map);
    
    const restaurantLatForBounds = routePathRestaurantLat?.lat ?? validRestaurantLat;
    const restaurantLngForBounds = routePathRestaurantLat?.lng ?? validRestaurantLng;
    
    if (restaurantLatForBounds && restaurantLngForBounds && validDeliveryLat && validDeliveryLng) {
      const bounds: [[number, number], [number, number]] = [
        [
          Math.min(restaurantLngForBounds, validDeliveryLng),
          Math.min(restaurantLatForBounds, validDeliveryLat),
        ],
        [
          Math.max(restaurantLngForBounds, validDeliveryLng),
          Math.max(restaurantLatForBounds, validDeliveryLat),
        ],
      ];
      
      map.fitBounds(bounds, { padding: 50, duration: 0 });
    } else if (validRestaurantLat && validRestaurantLng && validDeliveryLat && validDeliveryLng) {
      const bounds: [[number, number], [number, number]] = [
        [
          Math.min(validRestaurantLng, validDeliveryLng),
          Math.min(validRestaurantLat, validDeliveryLat),
        ],
        [
          Math.max(validRestaurantLng, validDeliveryLng),
          Math.max(validRestaurantLat, validDeliveryLat),
        ],
      ];
      
      map.fitBounds(bounds, { padding: 50, duration: 0 });
    }
  }, [validRestaurantLat, validRestaurantLng, validDeliveryLat, validDeliveryLng, routePathRestaurantLat]);

  const routeGeoJson = useMemo(() => {
    if (routePath && routePath.length > 0) {
      console.log('🗺️ Processing routePath (raw):', { 
        length: routePath.length, 
        firstPoint: routePath[0], 
        lastPoint: routePath[routePath.length - 1],
        firstPointType: typeof routePath[0],
        isFirstArray: Array.isArray(routePath[0]),
      });
      
      const coordinates = routePath
        .map((point, index) => {
          if (!Array.isArray(point) || point.length < 2) {
            console.warn(`⚠️ Invalid route point format at index ${index}:`, point);
            return null;
          }
          
          const lat = Number(point[0]);
          const lng = Number(point[1]);
          
          if (
            !isNaN(lat) &&
            !isNaN(lng) &&
            lat >= -90 &&
            lat <= 90 &&
            lng >= -180 &&
            lng <= 180
          ) {
            const geoJsonCoord: [number, number] = [lng, lat];
            if (index === 0 || index === routePath.length - 1) {
              console.log(`📍 Route point ${index === 0 ? 'FIRST' : 'LAST'}: [lat=${lat}, lng=${lng}] → GeoJSON [lng=${lng}, lat=${lat}]`);
            }
            return geoJsonCoord;
          }
          console.warn(`⚠️ Invalid route coordinates at index ${index}:`, { lat, lng, point });
          return null;
        })
        .filter((coord): coord is [number, number] => coord !== null);
      
      console.log('🗺️ Valid coordinates extracted:', { 
        count: coordinates.length, 
        first: coordinates[0], 
        last: coordinates[coordinates.length - 1],
        firstAsLatLng: coordinates[0] ? `lat=${coordinates[0][1]}, lng=${coordinates[0][0]}` : 'null',
        lastAsLatLng: coordinates[coordinates.length - 1] ? `lat=${coordinates[coordinates.length - 1][1]}, lng=${coordinates[coordinates.length - 1][0]}` : 'null',
      });
      
      if (coordinates.length < 2) {
        console.warn('⚠️ Insufficient valid coordinates in routePath, falling back to straight line');
        if (validRestaurantLat && validRestaurantLng && validDeliveryLat && validDeliveryLng) {
          return {
            type: 'Feature' as const,
            geometry: {
              type: 'LineString' as const,
              coordinates: [
                [validRestaurantLng, validRestaurantLat],
                [validDeliveryLng, validDeliveryLat],
              ],
            },
            properties: {},
          };
        }
        return null;
      }
      
      const geoJson = {
        type: 'Feature' as const,
        geometry: {
          type: 'LineString' as const,
          coordinates,
        },
        properties: {},
      };
      
      const firstCoordLat = coordinates[0] ? coordinates[0][1] : null;
      const firstCoordLng = coordinates[0] ? coordinates[0][0] : null;
      const lastCoordLat = coordinates[coordinates.length - 1] ? coordinates[coordinates.length - 1][1] : null;
      const lastCoordLng = coordinates[coordinates.length - 1] ? coordinates[coordinates.length - 1][0] : null;
      
      const firstCoordMatchesRestaurant = validRestaurantLat && validRestaurantLng && firstCoordLat && firstCoordLng
        ? Math.abs(firstCoordLat - validRestaurantLat) < 0.001 && Math.abs(firstCoordLng - validRestaurantLng) < 0.001
        : false;
      const lastCoordMatchesDelivery = validDeliveryLat && validDeliveryLng && lastCoordLat && lastCoordLng
        ? Math.abs(lastCoordLat - validDeliveryLat) < 0.001 && Math.abs(lastCoordLng - validDeliveryLng) < 0.001
        : false;
      
      console.log('✅ Route GeoJSON created:', { 
        type: geoJson.type, 
        coordinateCount: coordinates.length,
        firstGeoJsonCoord: coordinates[0],
        lastGeoJsonCoord: coordinates[coordinates.length - 1],
        firstAsLatLng: coordinates[0] ? `lat=${firstCoordLat}, lng=${firstCoordLng}` : 'null',
        lastAsLatLng: coordinates[coordinates.length - 1] ? `lat=${lastCoordLat}, lng=${lastCoordLng}` : 'null',
        expectedFirstLat: validRestaurantLat,
        expectedFirstLng: validRestaurantLng,
        expectedLastLat: validDeliveryLat,
        expectedLastLng: validDeliveryLng,
        firstCoordMatchesRestaurant,
        lastCoordMatchesDelivery,
        warning: !firstCoordMatchesRestaurant ? '⚠️ Route first point does NOT match restaurant location!' : undefined,
      });
      return geoJson;
    } else if (validRestaurantLat && validRestaurantLng && validDeliveryLat && validDeliveryLng) {
      console.log('🗺️ No routePath provided, using straight line from restaurant to delivery');
      return {
        type: 'Feature' as const,
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [validRestaurantLng, validRestaurantLat],
            [validDeliveryLng, validDeliveryLat],
          ],
        },
        properties: {},
      };
    }
    return null;
  }, [routePath, validRestaurantLat, validRestaurantLng, validDeliveryLat, validDeliveryLng]);

  useEffect(() => {
    if (!mapInstance) return;

    const sourceId = 'route-source';
    const layerId = 'route-layer';

    const updateRoute = () => {
      if (!routeGeoJson) {
        const existingSource = mapInstance.getSource(sourceId) as GeoJSONSource | undefined;
        if (existingSource) {
          if (mapInstance.getLayer(`${layerId}-shadow`)) {
            mapInstance.removeLayer(`${layerId}-shadow`);
          }
          if (mapInstance.getLayer(`${layerId}-outline`)) {
            mapInstance.removeLayer(`${layerId}-outline`);
          }
          if (mapInstance.getLayer(layerId)) {
            mapInstance.removeLayer(layerId);
          }
          mapInstance.removeSource(sourceId);
        }
        console.log('🗺️ Route removed from map (no routeGeoJson)');
        return;
      }

      const source = mapInstance.getSource(sourceId) as GeoJSONSource | undefined;
      
      console.log('🗺️ Updating route on map:', { 
        hasSource: !!source, 
        coordinateCount: routeGeoJson.geometry.coordinates.length,
        firstCoord: routeGeoJson.geometry.coordinates[0],
        lastCoord: routeGeoJson.geometry.coordinates[routeGeoJson.geometry.coordinates.length - 1]
      });
      
      if (source) {
        source.setData(routeGeoJson as any);
        
        const existingLayer = mapInstance.getLayer(layerId);
        if (existingLayer) {
          mapInstance.setPaintProperty(layerId, 'line-color', '#1E3A8A');
          mapInstance.setPaintProperty(layerId, 'line-width', 12);
          mapInstance.setPaintProperty(layerId, 'line-opacity', 1);
          mapInstance.setPaintProperty(layerId, 'line-dasharray', [1, 0]);
          const outlineLayer = mapInstance.getLayer(`${layerId}-outline`);
          if (outlineLayer) {
            mapInstance.setPaintProperty(`${layerId}-outline`, 'line-width', 14);
            mapInstance.setPaintProperty(`${layerId}-outline`, 'line-color', '#93C5FD');
            mapInstance.setPaintProperty(`${layerId}-outline`, 'line-opacity', 1);
            mapInstance.setPaintProperty(`${layerId}-outline`, 'line-dasharray', [1, 0]);
          }
          const shadowLayer = mapInstance.getLayer(`${layerId}-shadow`);
          if (shadowLayer) {
            mapInstance.setPaintProperty(`${layerId}-shadow`, 'line-width', 16);
            mapInstance.setPaintProperty(`${layerId}-shadow`, 'line-opacity', 0.2);
            mapInstance.setPaintProperty(`${layerId}-shadow`, 'line-blur', 4);
            mapInstance.setPaintProperty(`${layerId}-shadow`, 'line-dasharray', [1, 0]);
          }
        }
      } else {
        mapInstance.addSource(sourceId, {
          type: 'geojson',
          data: routeGeoJson as any,
        });

        const style = mapInstance.getStyle();
        const layers = style.layers || [];
        
        const firstSymbolLayer = layers.find(
          (layer) => layer.type === 'symbol' || layer.type === 'circle'
        );
        const beforeLayerId = firstSymbolLayer?.id;

        mapInstance.addLayer(
          {
            id: `${layerId}-shadow`,
            type: 'line',
            source: sourceId,
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': '#0f172a',
              'line-width': 16,
              'line-opacity': 0.2,
              'line-blur': 4,
              'line-dasharray': [1, 0],
            },
          },
          beforeLayerId
        );

        mapInstance.addLayer(
          {
            id: `${layerId}-outline`,
            type: 'line',
            source: sourceId,
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': '#93C5FD',
              'line-width': 14,
              'line-opacity': 1,
              'line-dasharray': [1, 0],
            },
          },
          beforeLayerId
        );

        mapInstance.addLayer(
          {
            id: layerId,
            type: 'line',
            source: sourceId,
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': '#1E3A8A',
              'line-width': 12,
              'line-opacity': 1,
              'line-dasharray': [1, 0],
            },
          },
          beforeLayerId
        );
        
        console.log('✅ Route layers added to map:', { shadow: `${layerId}-shadow`, outline: `${layerId}-outline`, main: layerId });
      }
    };

    if (mapInstance.isStyleLoaded()) {
      updateRoute();
    } else {
      mapInstance.once('styledata', updateRoute);
    }

    return () => {
      mapInstance.off('styledata', updateRoute);
    };
  }, [mapInstance, routeGeoJson]);

  return (
    <Card className="p-0 overflow-hidden">
      <div className="h-96 w-full relative">
        <MapLibreMap
          center={initialCenter}
          zoom={initialZoom}
          onMapLoad={handleMapLoad}
          className="w-full h-full"
          showControls={true}
          showGeolocate={false}
          showFullscreen={false}
        >
          {validRestaurantLat && validRestaurantLng && routePathRestaurantLat && (
            <MapMarker
              position={[validRestaurantLng, validRestaurantLat]}
              iconType="restaurant"
              size={40}
              popup={`Restoran - Lat: ${validRestaurantLat.toFixed(6)}, Lng: ${validRestaurantLng.toFixed(6)} (Route path'ten)`}
            />
          )}

          {validDeliveryLat && validDeliveryLng && (
            <MapMarker
              position={[validDeliveryLng, validDeliveryLat]}
              iconType="delivery"
              size={40}
              popup={`Teslimat Adresi - Lat: ${validDeliveryLat}, Lng: ${validDeliveryLng}`}
            />
          )}

          {showCourierMarker && courierPosition && (
            <MapMarker
              position={[courierPosition.lng, courierPosition.lat]}
              iconType="courier"
              size={48}
              bearing={courierPosition.bearing !== null && courierPosition.bearing !== undefined ? courierPosition.bearing : undefined}
              popup={
                `Kurye - Lat: ${courierPosition.lat.toFixed(6)}, Lng: ${courierPosition.lng.toFixed(6)}${
                  courierRemainingTime !== null && courierRemainingTime !== undefined
                    ? ` - Tahmini süre: ${Math.ceil(courierRemainingTime / 60)} dakika`
                    : ''
                }`
              }
            />
          )}
        </MapLibreMap>

        {showRecenterButton && showCourierMarker && smoothPosition && (
          <button
            onClick={handleRecenter}
            className="absolute bottom-4 right-4 bg-primary hover:bg-primary/90 text-white rounded-full p-3 shadow-lg z-[1000] transition-all duration-200 flex items-center justify-center"
            title="Kuryeyi takip et"
            aria-label="Re-center map on courier"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </button>
        )}

        <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3 z-[1000]">
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded-full"></div>
              <span>Restoran</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
              <span>Teslimat</span>
            </div>
            {showCourier && (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-amber-500 rounded-full animate-pulse"></div>
                <span>Kurye</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
